import React, { useState } from 'react';
import { updateClientAccountSecurity } from '../../api/clientPortal';
import { useClientAuth } from '../../contexts/ClientAuthContext';
import { useClientTheme } from '../../contexts/ClientThemeContext';
import { ErrorBanner } from './ClientDashboardPage';

export default function ClientAccountPage() {
  const { theme: T } = useClientTheme();
  const { clientUser, clientBrand, applySessionUpdate } = useClientAuth();
  const [form, setForm] = useState({
    username: clientUser?.username || clientUser?.email || '',
    authMethod: 'password',
    currentPassword: '',
    currentPin: '',
    newPassword: '',
    confirmPassword: '',
    newPin: '',
    confirmPin: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const card = {
    borderRadius: 10,
    border: `1px solid ${T.border}`,
    background: T.surface,
    padding: 18,
  };
  const labelStyle = {
    display: 'grid',
    gap: 7,
    color: T.label,
    fontSize: 10,
    fontWeight: 850,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
  };
  const inputStyle = {
    width: '100%',
    borderRadius: 8,
    border: `1px solid ${T.border}`,
    background: T.id === 'light' ? '#fff' : 'rgba(255,255,255,0.04)',
    color: T.text,
    fontSize: 14,
    padding: '11px 12px',
    outline: 'none',
  };

  const updateField = (key) => (event) => {
    setForm((prev) => ({ ...prev, [key]: event.target.value }));
    setError('');
    setMessage('');
  };

  const saveSecurity = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');

    const usingPin = form.authMethod === 'pin';
    if (usingPin && !clientUser?.securityPinEnabled) {
      setError('Set up a security code before using it to authorize changes.');
      return;
    }
    if (usingPin && !/^\d{4}$/.test(form.currentPin)) {
      setError('Enter your current 4-digit security code.');
      return;
    }
    if (!usingPin && !form.currentPassword) {
      setError('Enter your current password.');
      return;
    }
    if (form.newPassword || form.confirmPassword) {
      if (form.newPassword.length < 8) {
        setError('New password must be at least 8 characters.');
        return;
      }
      if (form.newPassword !== form.confirmPassword) {
        setError('New passwords do not match.');
        return;
      }
    }
    if (form.newPin || form.confirmPin) {
      if (!/^\d{4}$/.test(form.newPin)) {
        setError('Security code must be exactly 4 digits.');
        return;
      }
      if (form.newPin !== form.confirmPin) {
        setError('Security codes do not match.');
        return;
      }
    }

    setSaving(true);
    try {
      const data = await updateClientAccountSecurity({
        currentPassword: usingPin ? undefined : form.currentPassword,
        currentPin: usingPin ? form.currentPin : undefined,
        username: form.username,
        newPassword: form.newPassword || undefined,
        newPin: form.newPin || undefined,
      });
      applySessionUpdate(data);
      setForm((prev) => ({
        ...prev,
        username: data?.user?.username || prev.username,
        currentPassword: '',
        currentPin: '',
        newPassword: '',
        confirmPassword: '',
        newPin: '',
        confirmPin: '',
      }));
      setMessage('Account security updated.');
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not update account security.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 20, fontWeight: 750, color: T.text }}>Account</div>
        <div style={{ fontSize: 13, color: T.textSub, marginTop: 4 }}>
          Manage your {clientBrand?.name || 'Studio Sivad'} portal login.
        </div>
      </div>

      <section style={{ ...card, marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label, marginBottom: 14 }}>Profile</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
          <InfoTile label="Name" value={clientUser?.name || 'Not set'} T={T} />
          <InfoTile label="Email" value={clientUser?.email || 'Not set'} T={T} />
          <InfoTile label="Brand" value={clientBrand?.name || 'Not set'} T={T} />
        </div>
      </section>

      <form onSubmit={saveSecurity} style={{ ...card, display: 'grid', gap: 14, maxWidth: 620 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label }}>Security</div>
          <div style={{ fontSize: 13, color: T.textSub, marginTop: 5 }}>
            Change the temporary password after your first login, or set a 4-digit security code for future changes.
          </div>
        </div>

        {error ? <ErrorBanner T={T}>{error}</ErrorBanner> : null}
        {message ? (
          <div style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid rgba(34,197,94,0.35)', background: 'rgba(34,197,94,0.08)', color: '#22c55e', fontSize: 13 }}>
            {message}
          </div>
        ) : null}

        <label style={labelStyle}>
          Username
          <input type="text" value={form.username} onChange={updateField('username')} style={inputStyle} autoCapitalize="none" autoCorrect="off" />
        </label>

        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{ fontSize: 10, color: T.label, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 850 }}>Authorize Changes With</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {[
              ['password', 'Current password'],
              ['pin', clientUser?.securityPinEnabled ? '4-digit code' : '4-digit code not set'],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                disabled={value === 'pin' && !clientUser?.securityPinEnabled}
                onClick={() => setForm((prev) => ({ ...prev, authMethod: value }))}
                style={{
                  borderRadius: 8,
                  border: `1px solid ${form.authMethod === value ? T.gold : T.border}`,
                  background: form.authMethod === value ? 'rgba(201,168,76,0.14)' : (T.id === 'light' ? 'rgba(0,0,0,0.025)' : 'rgba(255,255,255,0.04)'),
                  color: value === 'pin' && !clientUser?.securityPinEnabled ? T.textMuted : T.text,
                  cursor: value === 'pin' && !clientUser?.securityPinEnabled ? 'not-allowed' : 'pointer',
                  fontSize: 12,
                  fontWeight: 800,
                  padding: '9px 11px',
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {form.authMethod === 'pin' ? (
          <label style={labelStyle}>
            Current 4-Digit Code
            <input type="password" value={form.currentPin} onChange={updateField('currentPin')} style={inputStyle} inputMode="numeric" pattern="[0-9]*" maxLength={4} autoComplete="one-time-code" />
          </label>
        ) : (
          <label style={labelStyle}>
            Current Password
            <input type="password" value={form.currentPassword} onChange={updateField('currentPassword')} style={inputStyle} autoComplete="current-password" />
          </label>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12 }}>
          <label style={labelStyle}>
            New Password
            <input type="password" value={form.newPassword} onChange={updateField('newPassword')} style={inputStyle} autoComplete="new-password" />
          </label>
          <label style={labelStyle}>
            Confirm New Password
            <input type="password" value={form.confirmPassword} onChange={updateField('confirmPassword')} style={inputStyle} autoComplete="new-password" />
          </label>
        </div>

        <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 14, display: 'grid', gap: 12 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 850, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.label }}>4-Digit Security Code</div>
            <div style={{ fontSize: 12, color: T.textSub, marginTop: 5 }}>
              {clientUser?.securityPinEnabled ? 'A security code is active. Enter a new one to replace it.' : 'No security code is set yet.'}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
            <label style={labelStyle}>
              New 4-Digit Code
              <input type="password" value={form.newPin} onChange={updateField('newPin')} style={inputStyle} inputMode="numeric" pattern="[0-9]*" maxLength={4} autoComplete="one-time-code" />
            </label>
            <label style={labelStyle}>
              Confirm Code
              <input type="password" value={form.confirmPin} onChange={updateField('confirmPin')} style={inputStyle} inputMode="numeric" pattern="[0-9]*" maxLength={4} autoComplete="one-time-code" />
            </label>
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          style={{
            justifySelf: 'start',
            borderRadius: 8,
            border: '1px solid rgba(201,168,76,0.55)',
            background: T.gold,
            color: '#101010',
            fontWeight: 850,
            fontSize: 13,
            padding: '11px 16px',
            cursor: saving ? 'wait' : 'pointer',
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? 'Saving...' : 'Save Security'}
        </button>
      </form>
    </div>
  );
}

function InfoTile({ label, value, T }) {
  return (
    <div style={{ borderRadius: 8, border: `1px solid ${T.border}`, padding: 12 }}>
      <div style={{ fontSize: 10, color: T.label, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 800 }}>{label}</div>
      <div style={{ fontSize: 14, color: T.text, fontWeight: 750, marginTop: 5, overflowWrap: 'anywhere' }}>{value}</div>
    </div>
  );
}
